"use client";

import { c as _c } from "react/compiler-runtime";
import { ImagePlus, Loader2, Sparkles, X } from "lucide-react";
import { type Dispatch, type SetStateAction, useCallback, useEffect, useRef, useState } from "react";
import { AspectRatioIcon } from "@/components/playground/aspect-ratio-icon";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { getModelImageConfig } from "@/lib/image-gen";
import type { AspectRatio } from "@/lib/image-gen";
interface InputImage {
  dataUrl: string;
  mediaType: string;
}
interface ImageControlsProps {
  prompt: string;
  setPrompt: (prompt: string) => void;
  selectedModels: string[];
  imageAspectRatio: AspectRatio;
  setImageAspectRatio: (value: AspectRatio) => void;
  imageSize: string;
  setImageSize: (value: string) => void;
  alibabaImageSize: string;
  setAlibabaImageSize: (value: string) => void;
  imageQuality: string;
  setImageQuality: (value: string) => void;
  imageCount: 1 | 2 | 3 | 4;
  setImageCount: (value: 1 | 2 | 3 | 4) => void;
  isGenerating: boolean;
  onGenerate: () => void;
  isEditModel: boolean;
  requiresImageInput: boolean;
  inputImages: InputImage[];
  setInputImages: Dispatch<SetStateAction<InputImage[]>>;
}
const aspectRatios: AspectRatio[] = ["auto", "1:1", "9:16", "16:9", "3:4", "4:3", "3:2", "2:3", "5:4", "4:5", "21:9", "1:4", "4:1", "1:8", "8:1"];
export function ImageControls(t0) {
  const $ = _c(95);
  const {
    prompt,
    setPrompt,
    selectedModels,
    imageAspectRatio,
    setImageAspectRatio,
    imageSize,
    setImageSize,
    alibabaImageSize,
    setAlibabaImageSize,
    imageQuality,
    setImageQuality,
    imageCount,
    setImageCount,
    isGenerating,
    onGenerate,
    isEditModel,
    requiresImageInput,
    inputImages,
    setInputImages
  } = t0;
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const primaryModel = selectedModels[0] ?? "";
  const config = getModelImageConfig(primaryModel);
  const maxInputImages = config.maxInputImages;
  let t1;
  if ($[0] !== isEditModel || $[1] !== maxInputImages || $[2] !== setInputImages) {
    t1 = file => {
      if (!file.type.startsWith("image/")) {
        return;
      }
      if (!isEditModel) {
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        setInputImages(prev => {
          if (prev.length >= maxInputImages) {
            return prev;
          }
          return [...prev, {
            dataUrl: reader.result as string,
            mediaType: file.type
          }];
        });
      };
      reader.readAsDataURL(file);
    };
    $[0] = isEditModel;
    $[1] = maxInputImages;
    $[2] = setInputImages;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const addImageFile = t1;
  let t2;
  if ($[4] !== isGenerating || $[5] !== onGenerate || $[6] !== prompt) {
    t2 = e => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        if (prompt.trim() && !isGenerating && canGenerate) {
          onGenerate();
        }
      }
    };
    $[4] = isGenerating;
    $[5] = onGenerate;
    $[6] = prompt;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const handleKeyDown = t2;
  let t3;
  if ($[8] !== addImageFile || $[9] !== inputImages.length || $[10] !== maxInputImages) {
    t3 = e_0 => {
      const files = Array.from(e_0.target.files ?? []);
      for (const file_0 of files) {
        if (inputImages.length >= maxInputImages) {
          break;
        }
        addImageFile(file_0);
      }
      e_0.target.value = "";
    };
    $[8] = addImageFile;
    $[9] = inputImages.length;
    $[10] = maxInputImages;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const handleFileSelect = t3;
  let t4;
  if ($[12] !== addImageFile || $[13] !== inputImages.length || $[14] !== isEditModel || $[15] !== maxInputImages) {
    t4 = e_1 => {
      if (!isEditModel || inputImages.length >= maxInputImages) {
        return;
      }
      const items = Array.from(e_1.clipboardData.items);
      for (const item of items) {
        if (item.type.startsWith("image/")) {
          e_1.preventDefault();
          const file_1 = item.getAsFile();
          if (file_1) {
            addImageFile(file_1);
          }
          break;
        }
      }
    };
    $[12] = addImageFile;
    $[13] = inputImages.length;
    $[14] = isEditModel;
    $[15] = maxInputImages;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  const handlePaste = t4;
  let t5;
  if ($[17] !== inputImages.length || $[18] !== isEditModel || $[19] !== maxInputImages) {
    t5 = e_2 => {
      e_2.preventDefault();
      e_2.stopPropagation();
      if (isEditModel && inputImages.length < maxInputImages) {
        setIsDragging(true);
      }
    };
    $[17] = inputImages.length;
    $[18] = isEditModel;
    $[19] = maxInputImages;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  const handleDragOver = t5;
  let t6;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = e_3 => {
      e_3.preventDefault();
      e_3.stopPropagation();
      if (dropZoneRef.current && !dropZoneRef.current.contains(e_3.relatedTarget as Node)) {
        setIsDragging(false);
      }
    };
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  const handleDragLeave = t6;
  let t7;
  if ($[22] !== addImageFile || $[23] !== inputImages.length || $[24] !== isEditModel || $[25] !== maxInputImages) {
    t7 = e_4 => {
      e_4.preventDefault();
      e_4.stopPropagation();
      setIsDragging(false);
      if (!isEditModel || inputImages.length >= maxInputImages) {
        return;
      }
      const files_0 = Array.from(e_4.dataTransfer.files);
      for (const file_2 of files_0) {
        if (file_2.type.startsWith("image/")) {
          addImageFile(file_2);
        }
      }
    };
    $[22] = addImageFile;
    $[23] = inputImages.length;
    $[24] = isEditModel;
    $[25] = maxInputImages;
    $[26] = t7;
  } else {
    t7 = $[26];
  }
  const handleDrop = t7;
  let t8;
  let t9;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      const handleWindowDragEnd = () => setIsDragging(false);
      window.addEventListener("dragend", handleWindowDragEnd);
      return () => window.removeEventListener("dragend", handleWindowDragEnd);
    };
    t9 = [];
    $[27] = t8;
    $[28] = t9;
  } else {
    t8 = $[27];
    t9 = $[28];
  }
  useEffect(t8, t9);
  let t10;
  let t11;
  if ($[29] !== maxInputImages || $[30] !== setInputImages) {
    t10 = () => {
      setInputImages(prev_0 => prev_0.length > maxInputImages ? prev_0.slice(0, maxInputImages) : prev_0);
    };
    t11 = [maxInputImages, setInputImages];
    $[29] = maxInputImages;
    $[30] = setInputImages;
    $[31] = t10;
    $[32] = t11;
  } else {
    t10 = $[31];
    t11 = $[32];
  }
  useEffect(t10, t11);
  let t12;
  if ($[33] !== setInputImages) {
    t12 = index => {
      setInputImages(prev_1 => prev_1.filter((_, i) => i !== index));
    };
    $[33] = setInputImages;
    $[34] = t12;
  } else {
    t12 = $[34];
  }
  const removeImage = t12;
  const canGenerate = prompt.trim().length > 0 && selectedModels.length > 0;
  const t13 = "border-b bg-background p-4";
  const t14 = "max-w-4xl mx-auto space-y-3";
  const t15 = `rounded-md border-input border dark:bg-input/30 shadow-xs focus-within:ring-1 focus-within:ring-ring transition-colors ${isDragging ? "border-primary bg-primary/5 ring-1 ring-primary" : ""}`;
  let t16;
  if ($[35] !== inputImages.length || $[36] !== isDragging || $[37] !== isEditModel || $[38] !== maxInputImages) {
    t16 = isDragging && isEditModel && inputImages.length < maxInputImages && <div className="flex items-center justify-center py-4 text-sm text-muted-foreground"><ImagePlus className="h-4 w-4 mr-2" />Drop image here</div>;
    $[35] = inputImages.length;
    $[36] = isDragging;
    $[37] = isEditModel;
    $[38] = maxInputImages;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[40] !== inputImages || $[41] !== isEditModel || $[42] !== removeImage) {
    t17 = isEditModel && inputImages.length > 0 && <div className="flex flex-wrap gap-2 px-3 pt-3">{inputImages.map((img, i_0) => <div key={i_0} className="relative group h-14 w-14 rounded-md border"><img src={img.dataUrl} alt={`Input ${i_0 + 1}`} className="size-full rounded-md object-cover" /><button type="button" aria-label="Remove attachment" onClick={() => removeImage(i_0)} className="absolute -top-1.5 -right-1.5 flex h-5 w-5 items-center justify-center rounded-full border bg-background text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity hover:text-foreground"><X className="h-3 w-3" /></button></div>)}</div>;
    $[40] = inputImages;
    $[41] = isEditModel;
    $[42] = removeImage;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  let t18;
  if ($[44] !== setPrompt) {
    t18 = e_5 => setPrompt(e_5.target.value);
    $[44] = setPrompt;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  const t19 = requiresImageInput ? "Describe how to edit the image... (paste or drop an image)" : isEditModel ? "Describe the image you want to generate... (optionally paste or drop an image)" : "Describe the image you want to generate...";
  let t20;
  if ($[46] !== handleKeyDown || $[47] !== handlePaste || $[48] !== isGenerating || $[49] !== prompt || $[50] !== t18 || $[51] !== t19) {
    t20 = <Textarea ref={textareaRef} value={prompt} onChange={t18} onKeyDown={handleKeyDown} onPaste={handlePaste} placeholder={t19} className="min-h-[80px] max-h-[200px] resize-none border-0 bg-transparent dark:bg-transparent focus-visible:ring-0 shadow-none" disabled={isGenerating} />;
    $[46] = handleKeyDown;
    $[47] = handlePaste;
    $[48] = isGenerating;
    $[49] = prompt;
    $[50] = t18;
    $[51] = t19;
    $[52] = t20;
  } else {
    t20 = $[52];
  }
  let t21;
  if ($[53] !== handleDragLeave || $[54] !== handleDragOver || $[55] !== handleDrop || $[56] !== t15 || $[57] !== t16 || $[58] !== t17 || $[59] !== t20) {
    t21 = <div ref={dropZoneRef} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} className={t15}>{t16}{t17}{t20}</div>;
    $[53] = handleDragLeave;
    $[54] = handleDragOver;
    $[55] = handleDrop;
    $[56] = t15;
    $[57] = t16;
    $[58] = t17;
    $[59] = t20;
    $[60] = t21;
  } else {
    t21 = $[60];
  }
  let t22;
  if ($[61] !== handleFileSelect) {
    t22 = <input ref={fileInputRef} type="file" accept="image/*" multiple={true} className="hidden" onChange={handleFileSelect} />;
    $[61] = handleFileSelect;
    $[62] = t22;
  } else {
    t22 = $[62];
  }
  const t23 = "flex flex-wrap items-center gap-2";
  let t24;
  if ($[63] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = () => fileInputRef.current?.click();
    $[63] = t24;
  } else {
    t24 = $[63];
  }
  const t25 = isGenerating || !isEditModel || inputImages.length >= maxInputImages;
  const t26 = !isEditModel ? "Image input not supported by selected model" : undefined;
  let t27;
  if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <ImagePlus className="h-4 w-4 mr-1.5" />;
    $[64] = t27;
  } else {
    t27 = $[64];
  }
  const t28 = inputImages.length === 0 ? "Add image" : `${inputImages.length}/${maxInputImages}`;
  let t29;
  if ($[65] !== t25 || $[66] !== t26 || $[67] !== t28) {
    t29 = <Button variant="outline" size="sm" onClick={t24} disabled={t25} title={t26}>{t27}{t28}</Button>;
    $[65] = t25;
    $[66] = t26;
    $[67] = t28;
    $[68] = t29;
  } else {
    t29 = $[68];
  }
  const t30 = !config.usesPixelDimensions && <><Select value={imageAspectRatio} onValueChange={val => setImageAspectRatio(val as AspectRatio)}><SelectTrigger size="sm" className="min-w-[110px]"><SelectValue placeholder="Aspect ratio" /></SelectTrigger><SelectContent>{aspectRatios.map(_temp)}</SelectContent></Select><Select value={imageSize} onValueChange={setImageSize}><SelectTrigger size="sm" className="min-w-[80px]"><SelectValue placeholder="Resolution" /></SelectTrigger><SelectContent>{config.availableSizes.map(_temp2)}</SelectContent></Select></>;
  const t31 = config.usesPixelDimensions && config.isGptImage && <Select value={alibabaImageSize} onValueChange={setAlibabaImageSize}><SelectTrigger size="sm" className="min-w-[130px]"><SelectValue placeholder="Resolution" /></SelectTrigger><SelectContent>{config.availableSizes.map(_temp3)}</SelectContent></Select>;
  const t32 = config.usesPixelDimensions && !config.isGptImage && <Select value={alibabaImageSize} onValueChange={setAlibabaImageSize}><SelectTrigger size="sm" className="min-w-[130px]"><SelectValue placeholder="Image Size" /></SelectTrigger><SelectContent><SelectItem value="1024x1024">1024x1024</SelectItem><SelectItem value="720x1280">720x1280</SelectItem><SelectItem value="1280x720">1280x720</SelectItem><SelectItem value="1024x1536">1024x1536</SelectItem><SelectItem value="1536x1024">1536x1024</SelectItem><SelectItem value="2048x1024">2048x1024</SelectItem><SelectItem value="1024x2048">1024x2048</SelectItem></SelectContent></Select>;
  const t33 = config.supportsQuality && <Select value={imageQuality} onValueChange={setImageQuality}><SelectTrigger size="sm" className="min-w-[100px]"><SelectValue placeholder="Quality" /></SelectTrigger><SelectContent>{config.availableQualities.map(_temp4)}</SelectContent></Select>;
  const t34 = String(imageCount);
  let t35;
  if ($[69] !== setImageCount) {
    t35 = val_0 => setImageCount(Number(val_0) as 1 | 2 | 3 | 4);
    $[69] = setImageCount;
    $[70] = t35;
  } else {
    t35 = $[70];
  }
  let t36;
  if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = <SelectTrigger size="sm" className="min-w-[90px]"><SelectValue placeholder="Count" /></SelectTrigger>;
    $[71] = t36;
  } else {
    t36 = $[71];
  }
  let t37;
  if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <SelectContent><SelectItem value="1">1 image</SelectItem><SelectItem value="2">2 images</SelectItem><SelectItem value="3">3 images</SelectItem><SelectItem value="4">4 images</SelectItem></SelectContent>;
    $[72] = t37;
  } else {
    t37 = $[72];
  }
  let t38;
  if ($[73] !== t34 || $[74] !== t35) {
    t38 = <Select value={t34} onValueChange={t35}>{t36}{t37}</Select>;
    $[73] = t34;
    $[74] = t35;
    $[75] = t38;
  } else {
    t38 = $[75];
  }
  let t39;
  if ($[76] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = <div className="flex-1" />;
    $[76] = t39;
  } else {
    t39 = $[76];
  }
  const t40 = isGenerating || !canGenerate;
  let t41;
  if ($[77] !== isGenerating) {
    t41 = isGenerating ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Generating...</> : <><Sparkles className="h-4 w-4 mr-2" />Generate</>;
    $[77] = isGenerating;
    $[78] = t41;
  } else {
    t41 = $[78];
  }
  let t42;
  if ($[79] !== onGenerate || $[80] !== t40 || $[81] !== t41) {
    t42 = <Button onClick={onGenerate} disabled={t40} className="min-w-[120px]">{t41}</Button>;
    $[79] = onGenerate;
    $[80] = t40;
    $[81] = t41;
    $[82] = t42;
  } else {
    t42 = $[82];
  }
  let t43;
  if ($[83] !== t29 || $[84] !== t30 || $[85] !== t31 || $[86] !== t32 || $[87] !== t33 || $[88] !== t38 || $[89] !== t42) {
    t43 = <div className={t23}>{t29}{t30}{t31}{t32}{t33}{t38}{t39}{t42}</div>;
    $[83] = t29;
    $[84] = t30;
    $[85] = t31;
    $[86] = t32;
    $[87] = t33;
    $[88] = t38;
    $[89] = t42;
    $[90] = t43;
  } else {
    t43 = $[90];
  }
  let t44;
  if ($[91] !== t21 || $[92] !== t22 || $[93] !== t43) {
    t44 = <div className={t13}><div className={t14}>{t21}{t22}{t43}</div></div>;
    $[91] = t21;
    $[92] = t22;
    $[93] = t43;
    $[94] = t44;
  } else {
    t44 = $[94];
  }
  return t44;
}
function _temp4(q) {
  return <SelectItem key={q} value={q}>{q.charAt(0).toUpperCase() + q.slice(1)}</SelectItem>;
}
function _temp3(size_0) {
  return <SelectItem key={size_0} value={size_0}>{size_0 === "auto" ? "Auto" : size_0}</SelectItem>;
}
function _temp2(size) {
  return <SelectItem key={size} value={size}>{size}</SelectItem>;
}
function _temp(r) {
  return <SelectItem key={r} value={r}><span className="flex items-center gap-2"><AspectRatioIcon ratio={r} />{r === "auto" ? "Auto" : r}</span></SelectItem>;
}